import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Sidebar from '@/components/layout/Sidebar';
import SuperAdminDashboard from '@/components/superadmin/SuperAdminDashboard';
import TeacherDashboard from '@/components/dashboard/TeacherDashboard';
import AdminDashboard from '@/components/dashboard/AdminDashboard';
import LessonPlanGenerator from '@/components/ai/LessonPlanGenerator';
import QuizGenerator from '@/components/ai/QuizGenerator';
import PlagiarismChecker from '@/components/ai/PlagiarismChecker';
import FloatingChatbot from '@/components/ai/FloatingChatbot';
import ClassesManager from '@/components/classes/ClassesManager';
import TeacherManagement from '@/components/admin/TeacherManagement';
import AssignmentManager from '@/components/assignments/AssignmentManager';
import QuestionsPoolManager from '@/components/questions/QuestionsPoolManager';
import AISlidesGenerator from '@/components/ai/AISlidesGenerator';
import GameifyManager from '@/components/gameify/GameifyManager';
import ErrorBoundary from '@/components/ErrorBoundary';
import { motion } from 'motion/react';
import { useIsMobile } from '@/hooks/use-mobile';

const SuperAdminPage: React.FC = () => {
  const { isSuperAdmin, adminStatus, hasAIAccess } = useAuth();
  const [activeTab, setActiveTab] = useState('superadmin');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const isMobile = useIsMobile();

  // Check if user has admin access (AdminL1, AdminL2, or SuperAdmin)
  const hasAdminAccess = isSuperAdmin || adminStatus.is_admin;

  // Redirect if not authorized - now allows AdminL1 access
  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100" data-id="sisdxxtwo" data-path="src/pages/SuperAdminPage.tsx">
        <div className="text-center" data-id="3cyv65rgg" data-path="src/pages/SuperAdminPage.tsx">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md" data-id="8itqo7xdx" data-path="src/pages/SuperAdminPage.tsx">
            <div className="text-red-500 mb-4" data-id="ybx3r46mp" data-path="src/pages/SuperAdminPage.tsx">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="ri78m3f43" data-path="src/pages/SuperAdminPage.tsx">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" data-id="koss81dzy" data-path="src/pages/SuperAdminPage.tsx" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-red-600 mb-4" data-id="erp08kit7" data-path="src/pages/SuperAdminPage.tsx">Access Denied</h1>
            <p className="text-red-500 mb-2" data-id="o5nto8iuw" data-path="src/pages/SuperAdminPage.tsx">You need admin privileges to access this feature.</p>
            <p className="text-sm text-red-400" data-id="sv9y5gtx3" data-path="src/pages/SuperAdminPage.tsx">
              Your current admin level: {adminStatus.admin_level || 0}
            </p>
            <p className="text-sm text-red-400" data-id="pimscvcu9" data-path="src/pages/SuperAdminPage.tsx">
              Required: Admin privileges (Level 1 or higher)
            </p>
          </div>
        </div>
      </div>);

  }

  const renderContent = () => {
    console.log('SuperAdminPage - renderContent called with activeTab:', activeTab);

    switch (activeTab) {
      case 'superadmin':
        return <SuperAdminDashboard data-id="g7ksua7vx" data-path="src/pages/SuperAdminPage.tsx" />;

      case 'dashboard':
        return isSuperAdmin ?
        <AdminDashboard setActiveTab={setActiveTab} data-id="1ciyw8gch" data-path="src/pages/SuperAdminPage.tsx" /> :
        <TeacherDashboard setActiveTab={setActiveTab} data-id="f5i3l8xqz" data-path="src/pages/SuperAdminPage.tsx" />;

      case 'classes':
        return <ClassesManager setActiveTab={setActiveTab} data-id="kqvhilqn5" data-path="src/pages/SuperAdminPage.tsx" />;

      case 'assignments':
        return <AssignmentManager data-id="1aje7rhc3" data-path="src/pages/SuperAdminPage.tsx" />;

      case 'questions-pool':
        console.log('SuperAdminPage - Rendering QuestionsPoolManager for admin user');
        return <QuestionsPoolManager data-id="9bojufmgl" data-path="src/pages/SuperAdminPage.tsx" />;

      case 'gameify':
        return (
          <ErrorBoundary data-id="w936qctz6" data-path="src/pages/SuperAdminPage.tsx">
            <GameifyManager data-id="7oa0k2dnb" data-path="src/pages/SuperAdminPage.tsx" />
          </ErrorBoundary>);


      case 'lesson-plans':
        return hasAIAccess('advanced') ?
        <ErrorBoundary data-id="fjoq9ajvv" data-path="src/pages/SuperAdminPage.tsx">
            <LessonPlanGenerator data-id="nnr07qnmi" data-path="src/pages/SuperAdminPage.tsx" />
          </ErrorBoundary> :
        <div className="p-6" data-id="vuan79gjh" data-path="src/pages/SuperAdminPage.tsx">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="c37te6l1q" data-path="src/pages/SuperAdminPage.tsx">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="lfecm23tv" data-path="src/pages/SuperAdminPage.tsx">Access Restricted</h2>
              <p className="text-yellow-700" data-id="9zfybe7z6" data-path="src/pages/SuperAdminPage.tsx">
                Lesson Plan Generator requires Tier 2 access. Please contact your administrator to upgrade your account.
              </p>
            </div>
          </div>;

      case 'quizzes':
        return hasAIAccess('advanced') ?
        <ErrorBoundary data-id="fhgyt65dy" data-path="src/pages/SuperAdminPage.tsx">
            <QuizGenerator data-id="am18i9hwg" data-path="src/pages/SuperAdminPage.tsx" />
          </ErrorBoundary> :
        <div className="p-6" data-id="nveyqpx1x" data-path="src/pages/SuperAdminPage.tsx">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="ikcatb8x5" data-path="src/pages/SuperAdminPage.tsx">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="gpvpnoa2x" data-path="src/pages/SuperAdminPage.tsx">Access Restricted</h2>
              <p className="text-yellow-700" data-id="kezhfdvxm" data-path="src/pages/SuperAdminPage.tsx">
                Quiz Generator requires Tier 2 access. Please contact your administrator to upgrade your account.
              </p>
            </div>
          </div>;

      case 'slides':
        return hasAIAccess('advanced') ?
        <ErrorBoundary data-id="jb5hlz90h" data-path="src/pages/SuperAdminPage.tsx">
            <AISlidesGenerator data-id="uwmspxu9n" data-path="src/pages/SuperAdminPage.tsx" />
          </ErrorBoundary> :
        <div className="p-6" data-id="nadnh4jix" data-path="src/pages/SuperAdminPage.tsx">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="3f1dq8170" data-path="src/pages/SuperAdminPage.tsx">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="6i6n120rx" data-path="src/pages/SuperAdminPage.tsx">Access Restricted</h2>
              <p className="text-yellow-700" data-id="hzccp3ysz" data-path="src/pages/SuperAdminPage.tsx">
                AI Slides Generator requires Tier 2 access. Please contact your administrator to upgrade your account.
              </p>
            </div>
          </div>;

      case 'plagiarism':
        return hasAIAccess('advanced') ?
        <ErrorBoundary data-id="ix6z36dsc" data-path="src/pages/SuperAdminPage.tsx">
            <PlagiarismChecker data-id="dnpcgg3wb" data-path="src/pages/SuperAdminPage.tsx" />
          </ErrorBoundary> :
        <div className="p-6" data-id="9cp2351mi" data-path="src/pages/SuperAdminPage.tsx">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="vivvui8qs" data-path="src/pages/SuperAdminPage.tsx">
              <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="px8ukmkgm" data-path="src/pages/SuperAdminPage.tsx">Access Restricted</h2>
              <p className="text-yellow-700" data-id="1mpzwg7mk" data-path="src/pages/SuperAdminPage.tsx">
                Plagiarism Checker requires Tier 2 access. Please contact your administrator to upgrade your account.
              </p>
            </div>
          </div>;

      case 'teachers':
        return <TeacherManagement data-id="0x9ccw1ng" data-path="src/pages/SuperAdminPage.tsx" />;

      case 'schools':
        return (
          <div className="text-center py-12" data-id="vxi91wpds" data-path="src/pages/SuperAdminPage.tsx">
            <h2 className="text-2xl font-bold mb-4" data-id="5xv4kobh7" data-path="src/pages/SuperAdminPage.tsx">School Management</h2>
            <p className="text-gray-600" data-id="66mtlvfm6" data-path="src/pages/SuperAdminPage.tsx">School management interface coming soon...</p>
          </div>);


      case 'settings':
        return (
          <div className="text-center py-12" data-id="f734z8cmz" data-path="src/pages/SuperAdminPage.tsx">
            <h2 className="text-2xl font-bold mb-4" data-id="cu4mbtq3m" data-path="src/pages/SuperAdminPage.tsx">System Settings</h2>
            <p className="text-gray-600" data-id="2s4az57nd" data-path="src/pages/SuperAdminPage.tsx">Settings interface coming soon...</p>
          </div>);


      case 'chat':
        return (
          <div className="p-6" data-id="nxdco0wpu" data-path="src/pages/SuperAdminPage.tsx">
            <h1 className="text-2xl font-bold text-gray-900 mb-4" data-id="y2il3c81r" data-path="src/pages/SuperAdminPage.tsx">AI Assistant</h1>
            {hasAIAccess('basic') ?
            <>
                <p className="text-gray-600 mb-6" data-id="nnx023rgg" data-path="src/pages/SuperAdminPage.tsx">
                  The AI Assistant is available via the floating chat button in the bottom-right corner.
                  You can click on it to start a conversation with our AI teaching assistant.
                </p>
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-lg" data-id="ag1wasbst" data-path="src/pages/SuperAdminPage.tsx">
                  <h2 className="text-lg font-semibold text-blue-900 mb-3" data-id="hjvhybzoj" data-path="src/pages/SuperAdminPage.tsx">What can the AI Assistant help with?</h2>
                  <ul className="text-blue-800 space-y-2" data-id="didat2pha" data-path="src/pages/SuperAdminPage.tsx">
                    <li data-id="p07e6vjcv" data-path="src/pages/SuperAdminPage.tsx">• General teaching questions and advice</li>
                    <li data-id="xchvc6zbi" data-path="src/pages/SuperAdminPage.tsx">• Educational resource recommendations</li>
                    <li data-id="63wi1vlb5" data-path="src/pages/SuperAdminPage.tsx">• Classroom management tips</li>
                    <li data-id="r5l4rt0ln" data-path="src/pages/SuperAdminPage.tsx">• Student engagement techniques</li>
                    {hasAIAccess('advanced') &&
                  <>
                        <li data-id="uidldakgf" data-path="src/pages/SuperAdminPage.tsx">• Lesson planning and curriculum development</li>
                        <li data-id="sf9sieisz" data-path="src/pages/SuperAdminPage.tsx">• Quiz and assessment creation</li>
                        <li data-id="dnl7gkf2j" data-path="src/pages/SuperAdminPage.tsx">• Advanced teaching strategies</li>
                      </>
                  }
                  </ul>
                </div>
              </> :
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="tpthvnoyz" data-path="src/pages/SuperAdminPage.tsx">
                <h2 className="text-lg font-semibold text-yellow-800 mb-2" data-id="oicl6dzf7" data-path="src/pages/SuperAdminPage.tsx">Access Restricted</h2>
                <p className="text-yellow-700" data-id="0waxprq2i" data-path="src/pages/SuperAdminPage.tsx">
                  AI Assistant requires at least Tier 1 access. Please contact your administrator to upgrade your account.
                </p>
              </div>
            }
          </div>);


      default:
        console.log('SuperAdminPage - Default case hit with activeTab:', activeTab);
        return (
          <div className="text-center py-12" data-id="r0cnlfhki" data-path="src/pages/SuperAdminPage.tsx">
            <h2 className="text-2xl font-bold mb-4" data-id="ni6f5q1i3" data-path="src/pages/SuperAdminPage.tsx">Page Not Found</h2>
            <p className="text-gray-600" data-id="0ah0scjd8" data-path="src/pages/SuperAdminPage.tsx">The requested page could not be found.</p>
            <p className="text-sm text-gray-500 mt-2" data-id="inllx8qze" data-path="src/pages/SuperAdminPage.tsx">Active tab: {activeTab}</p>
          </div>);

    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-id="ipe0ijn4w" data-path="src/pages/SuperAdminPage.tsx">
      <Navbar data-id="vrxgotp0w" data-path="src/pages/SuperAdminPage.tsx" />
      <div className={`flex h-[calc(100vh-4rem)] ${isMobile ? 'flex-col' : ''}`} data-id="kede8t5w6" data-path="src/pages/SuperAdminPage.tsx">
        {/* Desktop: Show sidebar normally, Mobile: Sidebar becomes bottom nav */}
        {!isMobile &&
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed} data-id="r77qa8z9t" data-path="src/pages/SuperAdminPage.tsx" />
        }

        <main className={`flex-1 overflow-auto transition-all duration-300 ${
        isMobile ?
        'pb-20' // Add bottom padding on mobile for bottom nav
        : isSidebarCollapsed ?
        'ml-0' :
        'ml-0'}`
        } data-id="ui205ywv6" data-path="src/pages/SuperAdminPage.tsx">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={`${isMobile ? 'p-3' : 'p-6'} max-w-7xl mx-auto`} data-id="rzysjp11n" data-path="src/pages/SuperAdminPage.tsx">
            {renderContent()}
          </motion.div>
        </main>

        {/* Mobile: Show bottom navigation */}
        {isMobile &&
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed} data-id="gehh7ogcd" data-path="src/pages/SuperAdminPage.tsx" />
        }
      </div>
      
      {/* Floating Chatbot - Available on all pages, adjusted for mobile */}
      <div className={isMobile ? 'mb-20' : ''} data-id="6hfjqjeek" data-path="src/pages/SuperAdminPage.tsx">
        <FloatingChatbot data-id="or32wpdw0" data-path="src/pages/SuperAdminPage.tsx" />
      </div>
    </div>);

};

export default SuperAdminPage;